import React from 'react';
import doneMedal from '@/assets/images/doneMedal.svg';
import brilhoDireita from '@/assets/images/icons/rightBtnIcon.svg';
import brilhoEsquerda from '@/assets/images/icons/leftBtnIcon.svg';
import { useNavigate } from 'react-router-dom';
import { CardProgress, CardProgressBar, CardProgressContainer } from '@/components/ui/card';
import { MetricCard, MetricCardBody, MetricCardDescription, MetricCardMedal, MetricCardTitle } from '@/components/custom/metric';
import { Button, ButtonIcon, ButtonText } from '@/components/ui/button';
import { isPlayfulTheme } from '@/lib/layout-version';
import ilustraDiarias from '@/assets/images/conquistas/diarias.png';
import ilustraMissoes from '@/assets/images/conquistas/missoes.png';
import ilustraMerito from '@/assets/images/conquistas/merito.png';

// Estilo de card lúdico (igual aos cards da Home) por categoria: fundo pastel,
// arredondado, sombra suave, sem borda dura — e uma ilustração 3D por card.
const PLAYFUL_CARD = {
  achievements: { bg: 'var(--color-bg-lilac)', illustration: ilustraDiarias, petal: '#7B63BF' },
  missions: { bg: 'var(--color-bg-amber-soft)', illustration: ilustraMissoes, petal: '#F99153' },
  merit: { bg: 'var(--color-bg-rose-soft, #FFE7E5)', illustration: ilustraMerito, petal: '#FF4C6F' }
};

// Progresso circular em pétalas: anel dividido em N segmentos (= stages), os
// concluídos preenchidos na cor da categoria, com número no centro. Substitui a
// barra linear no infantil. 1 stage = anel cheio; vários = pétalas em volta.
function PetalProgress({ total, current, color }) {
  const count = Math.max(total || 1, 1);
  const done = Math.max(Math.min(current || 0, count), 0);
  const cx = 50;
  const cy = 50;
  const r = 37;
  const sw = 12;
  const empty = 'rgba(123, 99, 191, 0.15)';
  const polar = deg => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const seg = 360 / count;
  const gap = count > 1 ? 12 : 0;
  const arcPath = i => {
    const start = i * seg + gap / 2;
    const end = (i + 1) * seg - gap / 2;
    const [x1, y1] = polar(start);
    const [x2, y2] = polar(end);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${end - start > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-12 w-12 shrink-0"
      aria-hidden="true"
    >
      {count === 1 ? (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={done >= 1 ? color : empty}
          strokeWidth={sw}
        />
      ) : (
        Array.from({ length: count }).map((_, i) => (
          <path
            key={i}
            d={arcPath(i)}
            fill="none"
            stroke={i < done ? color : empty}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        ))
      )}
      <text
        x={cx}
        y={cy + 1}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="30"
        fontWeight="800"
        fill={color}
      >
        {done}
      </text>
    </svg>
  );
}

const redirectByAchievements = (title, navigate) => {
  const routes = {
    '/guia-de-estudos': ['Estudioso', 'Maratonista do Conhecimento', 'Memória de Aço', 'Explorador Completo'],
    '/curso': ['Aluno Dedicado', 'Mestre do Simulado'],
    '/feed': ['Participante Ativo'],
    '/desafio-da-semana/desafios': ['Desafiante da Semana', 'Conector de Ideias', 'Guerreiro dos Desafios', 'Campeão Consistente'],
    '/redacao': ['Escritor em Ação'],
    '/auditorio': ['Aluno Atento'],
    '/simulados': ['Mestre do Simulado']
  };

  for (const [route, titles] of Object.entries(routes)) {
    if (titles.includes(title)) {
      navigate(route);
      return;
    }
  }
};

function Card({ title, done = false, description, currentCount, totalCount, points, buttonClass }) {
  const navigate = useNavigate();
  const playful = isPlayfulTheme();
  const card = playful ? PLAYFUL_CARD[buttonClass] : null;

  const getButtonVariant = {
    achievements: 'primary-light',
    missions: 'mission',
    merit: 'merit'
  };

  const percentage = (currentCount / totalCount) * 100;

  const buttonEl = (
    <Button
      variant={done ? 'done' : getButtonVariant[buttonClass]}
      border={!done}
      onClick={() => {
        if (done) return null;
        redirectByAchievements(title, navigate);
      }}
      disabled={title === 'Maratona de Missões'}
      className="disabled:opacity-100"
    >
      {!done && (
        <ButtonIcon>
          <img
            src={brilhoEsquerda}
            alt="brilho a esquerda"
          />
        </ButtonIcon>
      )}

      <ButtonText>Ganhe {points} pontos</ButtonText>

      {!done && (
        <ButtonIcon>
          <img
            src={brilhoDireita}
            alt="brilho a direita"
          />
        </ButtonIcon>
      )}
    </Button>
  );

  // Conquista concluída fica toda verde (BG + donut + contador + botão); caso
  // contrário usa a cor da categoria.
  const DONE_GREEN = '#27AE60';
  const accent = done ? DONE_GREEN : card?.petal || '#7B63BF';
  const cardBg = done ? 'color-mix(in srgb, #27AE60 14%, #fff)' : card?.bg;

  // Botão lúdico no estilo da trilha: pill com gradiente, lip colorido (sombra
  // sólida deslocada), brilho no topo e leve afundar no clique. Lip e gradiente
  // derivam da cor do accent via color-mix.
  const btnAccent = accent;
  const playfulButton = (
    <button
      type="button"
      disabled={title === 'Maratona de Missões'}
      onClick={() => {
        if (done) return;
        redirectByAchievements(title, navigate);
      }}
      className="group/btn relative w-full overflow-hidden rounded-full py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97] disabled:opacity-60"
      style={{
        background: `linear-gradient(180deg, color-mix(in srgb, ${btnAccent} 88%, #fff) 0%, ${btnAccent} 100%)`,
        boxShadow: `0 4px 0 color-mix(in srgb, ${btnAccent} 62%, #000), 0 6px 14px rgba(0,0,0,0.16)`
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
      />
      <span className="relative flex items-center justify-center gap-2">
        {!done && (
          <img
            src={brilhoEsquerda}
            alt=""
            className="h-4 w-4"
          />
        )}
        {done ? 'Concluído' : `Ganhe ${points} pontos`}
        {!done && (
          <img
            src={brilhoDireita}
            alt=""
            className="h-4 w-4"
          />
        )}
      </span>
    </button>
  );

  // Layout lúdico: topo = [ícone à esquerda | círculo de pétalas à direita]
  // alinhados; abaixo, título, descrição e botão coladinhos. Brilho no topo do
  // card pro mesmo relevo dos cards da trilha.
  if (playful) {
    return (
      <MetricCard
        className="relative gap-1.5 overflow-hidden rounded-[24px] border-0 p-6 shadow-card"
        style={cardBg ? { background: cardBg } : undefined}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, transparent 100%)' }}
        />

        <div className="relative flex items-start justify-between gap-3">
          {card?.illustration && (
            <img
              src={card.illustration}
              alt=""
              aria-hidden="true"
              className="h-14 w-auto object-contain"
            />
          )}
          <div className="flex shrink-0 flex-col items-center">
            <PetalProgress
              total={totalCount}
              current={currentCount || 0}
              color={accent}
            />
            <span
              className="text-xs font-extrabold tabular-nums"
              style={{ color: accent }}
            >
              {currentCount || 0}/{totalCount}
            </span>
          </div>
        </div>

        <MetricCardTitle className="relative text-neutral-900">{title}</MetricCardTitle>

        <MetricCardDescription className="relative w-full max-w-full">{description}</MetricCardDescription>

        <div className="relative">{playfulButton}</div>
      </MetricCard>
    );
  }

  return (
    <MetricCard>
      <MetricCardTitle>{title}</MetricCardTitle>

      <MetricCardBody>
        {done && (
          <MetricCardMedal
            src={doneMedal}
            alt="concluído"
          />
        )}

        <MetricCardDescription>{description}</MetricCardDescription>

        <CardProgress className="w-[152px]">
          <CardProgressContainer>
            <CardProgressBar
              className="bg-[#6FCF97]"
              percentage={percentage}
            />
          </CardProgressContainer>
          <p className="mb-0 shrink-0">
            {currentCount}/{totalCount}
          </p>
        </CardProgress>

        {buttonEl}
      </MetricCardBody>
    </MetricCard>
  );
}

export default Card;
